import React from "react";
import { defaults } from "lodash-es";
import { Theme, StandardProps } from "@material-ui/core";
import { createStyles, makeStyles, useTheme } from "@material-ui/styles";
import { emphasize } from "@material-ui/core/styles/colorManipulator";
import classNames from "classnames";
import { FixedSizeListProps } from "react-window";
import { CustomSelectComponentSelectProps } from "./types";
import {
    Option,
    Control,
    MenuList,
    Menu,
    MultiValue,
    NoOptionsMessage,
    Placeholder,
    SingleValue,
    ValueContainer
} from "./CustomComponents";
import { StyleClassKey } from "../../typescript";
import { Props } from "react-select/src/Select";

const styles = (theme: Theme) =>
    createStyles({
        root: {
            "&$fullWidth": {
                width: "100%",
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: 4
            }
        },
        fullWidth: {},
        formControl: {
            height: "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: "30px",
            "&:hover": {
                // borderBottom: `1px solid ${theme.palette.primary.main}!important`
            }
        },
        formControlInput: {
            minHeight: "30px"
        },
        input: {
            display: "flex",
            height: "auto",
            padding: 0
        },
        controlContainer: {
            display: "flex",
            flexGrow: 1
        },
        valueContainer: {
            display: "flex",
            flexWrap: "wrap",
            flex: 1,
            alignItems: "center",
            "&>input": {
                //react select dummy input
                width: 0
            },
            "&>p": {
                //react select dummy input
                paddingLeft: 8,
                paddingRight: 8
            },
            "&>div": {
                // paddingLeft: 5,
                // paddingRight: 5
            }
        },
        noOptionsMessage: {
            padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`
        },
        singleValue: {
            fontSize: 16,
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1)
        },
        placeholder: {
            position: "absolute",
            left: 2,
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            fontSize: 16
        },
        paper: {
            border: `1px solid ${theme.palette.grey.A100}`,
            background: theme.palette.background.paper,
            position: "absolute",
            zIndex: 2,
            marginTop: theme.spacing(0.125),
            left: 0,
            right: 0
        },
        chip: {
            borderRadius: "0px",
            height: "auto",
            padding: theme.spacing(1) / 3,
            margin: `${theme.spacing(1) / 2}px ${theme.spacing(1) / 4}px`
        },
        chipLabel: {
            order: 0
        },
        chipFocused: {
            backgroundColor: emphasize(
                theme.palette.type === "light"
                    ? theme.palette.grey[300]
                    : theme.palette.grey[700],
                0.08
            )
        },
        deleteIconStyle: {
            // color: theme.custom.text.secondary,
            margin: "0px",
            order: 1,
            fontSize: theme.typography.pxToRem(10),
            width: theme.spacing(2),
            marginLeft: theme.spacing(-1)
        },
        reloadIcon: {
            alignSelf: "flex-start",
            padding: theme.spacing(1) / 2
        }
    });

export type CustomSelectClassKey = StyleClassKey<typeof styles>;

const useStyles = makeStyles(styles);

const ccomponents = {
    Control,
    // MenuList,
    Menu,
    MultiValue,
    NoOptionsMessage,
    Option,
    Placeholder,
    SingleValue,
    ValueContainer
};

interface PrivateProps extends StandardProps<{}, CustomSelectClassKey> {
    showDropdownIndicator?: boolean;
    fullWidth?: boolean;
    fixedSizeListProps?: FixedSizeListProps;
    allowReload?: boolean;
    onReload?: () => void;
}

export type CustomSelectProps<
    TProps extends Props<OptionType>,
    OptionType
> = PrivateProps &
    TProps & {
        Component: React.ComponentType<TProps>;
    };

function Component<
    TProps extends CustomSelectProps<SProps, OptionType>,
    SProps extends Props<OptionType>,
    OptionType
>(props: TProps) {
    let {
        styles,
        showDropdownIndicator,
        components,
        classes,
        fullWidth,
        allowReload,
        onReload,
        fixedSizeListProps
    } = props;
    classes = useStyles(props);
    const theme = useTheme<Theme>();

    defaults(components, {
        Control: ccomponents.Control,
        // MenuList: ccomponents.MenuList,
        Menu: ccomponents.Menu,
        MultiValue: ccomponents.MultiValue,
        NoOptionsMessage: ccomponents.NoOptionsMessage,
        Option: ccomponents.Option,
        Placeholder: ccomponents.Placeholder,
        SingleValue: ccomponents.SingleValue,
        ValueContainer: ccomponents.ValueContainer
    });

    const extraInjectedProps: CustomSelectComponentSelectProps = {
        classes,
        controlProps: {
            fullWidth: fullWidth,
            allowReload,
            onReload
        },
        fixedSizeListProps: fixedSizeListProps
    };

    const TComponent: React.ComponentType<SProps> = props.Component;

    return (
        <TComponent
            {...props}
            className={classNames(props.className, classes.root, {
                [classes.fullWidth]: fullWidth
            })}
            {...extraInjectedProps}
            styles={{
                indicatorsContainer: base => ({
                    ...base,
                    "&>div": {
                        padding: theme.spacing(0, 1, 0, 0)
                    }
                }),
                input: base => ({
                    ...base,
                    paddingLeft: theme.spacing(0.625),
                    paddingRight: theme.spacing(0.625)
                }),
                indicatorSeparator: base => ({
                    ...base,
                    display: "none"
                }),
                dropdownIndicator: base => ({
                    ...base,
                    display: !showDropdownIndicator ? "none" : undefined
                }),
                ...styles
            }}
        />
    );
}
Component.displayName = "CustomSelect";
Component.defaultProps = {
    menuShouldBlockScroll: false,
    showDropdownIndicator: true,
    hideSelectedOptions: false,
    components: ccomponents,
    filterOption: undefined,
    isMulti: false,
    isSearchable: true,
    placeholder: "",
    isClearable: false,
    allowReload: false,
    onReload: () => {},
    value: null,
    fullWidth: false
};

export const CustomSelect = Component;
export default CustomSelect;
